/**
 * 生成历史 Mixin
 *
 * 管理侧边栏历史记录：从后端拉取 + IndexedDB mediaCache（wan-unified 等上报空 aiResultUrl 时按 historyId 取本地 blob）+ 实时生成更新。
 * 使用方式：在 Image/VideoGeneratorNew 中 mixin 后即可。
 *
 * 前置依赖（由组件或 generationMixin 提供）：
 *   data.mixinType           - 'image' 或 'video'
 *   data.mixinDefaultAiName  - 默认模型名称
 *   computed.model           - 当前模型对象
 *   data.paramValues         - 参数对象（含 prompt）
 *   data.generatedVideoUrl   - 视频生成结果 URL
 *   data.generatedImageUrl   - 图片生成结果 URL
 */

var getAIHistory = require('../api/aiHistory').getAIHistory
var saveOrUpdateAIHistory = require('../api/aiHistory').saveOrUpdateAIHistory
var batchGetLocalMediaMaps = require('../utils/mediaCache').batchGetLocalMediaMaps

function trimStr(s) {
  return String(s || '').trim()
}

/**
 * 将 'YYYY-MM-DD HH:mm:ss' 格式的时间字符串转为时间戳毫秒数
 */
function _parseDateToMs(dateStr) {
  if (!dateStr) return 0
  var s = String(dateStr).trim()
  var m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})/)
  if (!m) return 0
  var d = new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
    parseInt(m[4], 10),
    parseInt(m[5], 10),
    parseInt(m[6], 10)
  )
  return d.getTime()
}

function looksLikeAudioUrl(u) {
  var s = trimStr(u).split('?')[0].toLowerCase()
  if (!s) return false
  return s.match(/\.(mp3|wav|m4a|aac|ogg)(\b|$)/) !== null || s.indexOf('blob:') === 0
}

function isMusicHistoryRaw(r) {
  if (!r || typeof r !== 'object') return false
  var mt = trimStr(r.aiMediaType).toLowerCase()
  if (mt === 'music' || mt === 'audio') return true
  var name = trimStr(r.aiName).toLowerCase()
  if (name === 'ai music' || name.indexOf('suno') !== -1) return true
  var url0 = trimStr(r.aiResultUrl || '').split(',')[0]
  if (url0 && looksLikeAudioUrl(url0)) return true
  return false
}

function splitMusicResultUrlString(raw) {
  var s = trimStr(raw)
  if (!s) return []
  return s.split(',').map(function (p) { return trimStr(p) }).filter(Boolean)
}

module.exports = {
  data: function () {
    return {
      historyItems: [],
      _localMediaMap: {},
      _localMultiMediaMap: {}
    }
  },

  methods: {
    _normalizeHistoryId: function (id) {
      if (id == null || id === '') return ''
      return String(id)
    },
    /**
     * 从后端拉取全部状态的历史记录；有远端 URL 直接用，无则按 historyId 从 IndexedDB 取 blob
     */
    _fetchHistory: function () {
      var self = this
      getAIHistory({ state: 0, pageNumber: 1, pageSize: 20 }).then(function (res) {
        var list = (res && res.historyList) || []
        var items = []
        var now = Date.now()
        /* 超过 10 分钟仍为 state=1 的记录视为过期失败，兜底保护避免永远转圈 */
        var STALE_LOADING_MS = 10 * 60 * 1000
        for (var i = 0; i < list.length; i++) {
          var r = list[i]
          /* 后端 state 映射：1 生成中 → loading, 2 成功 → completed, 3 失败 → failed */
          var status = 'loading'
          if (r.state === 2) status = 'completed'
          else if (r.state === 3) status = 'failed'
          else if (r.state === 1) {
            var createdMs = _parseDateToMs(r.createDate)
            if (createdMs && (now - createdMs > STALE_LOADING_MS)) {
              status = 'failed'
            }
          }
          var rawUrl = r.aiResultUrl != null && r.aiResultUrl !== '' ? String(r.aiResultUrl).trim() : ''
          var resultUrlsInit = []
          if (rawUrl) {
            if (isMusicHistoryRaw(r) && rawUrl.indexOf(',') !== -1) {
              resultUrlsInit = splitMusicResultUrlString(rawUrl)
            } else {
              resultUrlsInit = [rawUrl]
            }
          }
          var errorMsg = r.message || ''
          if (status === 'failed' && r.state === 1 && !errorMsg) {
            errorMsg = self.$t('common.genRecoveryTimeout') || 'Generation timed out'
          }
          items.push({
            status: status,
            resultUrl: resultUrlsInit.length ? resultUrlsInit[0] : '',
            resultUrls: resultUrlsInit,
            mediaType: r.aiMediaType || '',
            modelName: r.aiName || '',
            prompt: r.aiDesc || '',
            timestamp: r.createDate || '',
            error: errorMsg,
            historyId: self._normalizeHistoryId(r.historyId != null ? r.historyId : (r.id || '')),
            taskId: r.taskId || ''
          })
          /* 过期的 loading 记录，静默上报后端标记为失败，避免下次再拉到 state=1 */
          if (r.state === 1 && status === 'failed') {
            saveOrUpdateAIHistory({
              aiName: r.aiName || '',
              historyId: r.historyId != null ? String(r.historyId) : (r.id ? String(r.id) : ''),
              taskId: r.taskId || '',
              aiDesc: r.aiDesc || '',
              state: 3,
              message: 'Generation timed out',
              aiResultUrl: '',
              createDate: r.createDate || '',
              aiMediaType: r.aiMediaType || 'image'
            }).catch(function () {})
          }
        }
        /* API 返回的列表是最新在前（降序），反转使最旧在前，和 onGenerationStart 追加顺序一致 */
        items.reverse()
        self.historyItems = items
        /* 恢复生成任务期间，_fetchHistory 异步返回会覆盖掉 _recoverPendingTask 推入的 loading 条目
           如果此时仍在轮询中，需要确保末尾有 loading 条目供 onGenerationComplete 更新 */
        if (self.generating) {
          var lastItem = self.historyItems.length > 0 ? self.historyItems[self.historyItems.length - 1] : null
          if (!lastItem || lastItem.status !== 'loading') {
            self.historyItems.push({
              status: 'loading',
              mediaType: self.mixinType,
              modelName: (self.model && self.model.name) || self.mixinDefaultAiName,
              prompt: ''
            })
          }
        }
        return self._loadLocalMediaCache(self.historyItems)
      }).catch(function () {
        self.historyItems = []
      })
    },

    /**
     * 从 IndexedDB 批量取 blob：仅当该条尚无远端 resultUrl 时才回填（有 URL 则直连展示）
     */
    _loadLocalMediaCache: function (items) {
      var self = this
      var ids = []
      var seen = {}
      var walk = items || self.historyItems
      for (var i = 0; i < walk.length; i++) {
        var item = walk[i]
        if (item.status !== 'completed') continue
        if (String(item.resultUrl || '').trim() !== '') continue
        var cacheId = self._normalizeHistoryId(item.historyId)
        if (cacheId && !seen[cacheId]) {
          seen[cacheId] = true
          ids.push(cacheId)
        }
      }
      var p = Promise.resolve()
      if (ids.length) {
        p = batchGetLocalMediaMaps(ids).then(function (res) {
          self._localMediaMap = Object.assign({}, self._localMediaMap, res.single || {})
          self._localMultiMediaMap = Object.assign({}, self._localMultiMediaMap, res.multi || {})
          for (var j = 0; j < self.historyItems.length; j++) {
            var entry = self.historyItems[j]
            if (entry.status !== 'completed') continue
            if (String(entry.resultUrl || '').trim() !== '') continue
            var urls = self._getCachedUrls(entry.historyId)
            if (urls && urls.length) {
              self.$set(entry, 'resultUrls', urls)
              self.$set(entry, 'resultUrl', urls[0])
            }
          }
        })
      }
      return p.catch(function () {})
    },

    /**
     * 根据单条记录的 historyId/taskId 从本地缓存获取 Object URL
     */
    _getCachedUrls: function (historyId) {
      if (historyId) {
        var multi = this._localMultiMediaMap[historyId]
        if (multi && multi.length) return multi
        var single = this._localMediaMap[historyId]
        if (single) return [single]
      }
      return []
    },

    /**
     * 实时生成开始 — 插入一条 loading 记录
     */
    onGenerationStart: function () {
      this.historyItems.push({
        status: 'loading',
        mediaType: this.mixinType,
        modelName: (this.model && this.model.name) || this.mixinDefaultAiName,
        prompt: (this.paramValues.prompt || '').trim()
      })
    },

    /**
     * 实时生成完成 — 更新最后一条 loading 记录
     */
    onGenerationComplete: function (statusRes) {
      var urls = statusRes.urls || statusRes.url
      var resultUrls = []
      if (Array.isArray(urls) && urls.length > 0) resultUrls = urls
      else if (typeof urls === 'string') resultUrls = [urls]
      var resultUrl = resultUrls.length ? resultUrls[0] : ''
      var fallback = this.mixinType === 'video' ? this.generatedVideoUrl : this.generatedImageUrl
      this._updateLastEntry({
        status: 'completed',
        resultUrl: resultUrl || fallback,
        resultUrls: resultUrls.length ? resultUrls : (fallback ? [fallback] : [])
      })
    },

    /**
     * 实时生成失败 — 更新最后一条 loading 记录
     */
    onGenerationFailed: function (statusRes) {
      this._updateLastEntry({ status: 'failed', error: statusRes.message || statusRes.error || '' })
    },

    /**
     * 更新 historyItems 中最后一条 loading 记录的字段
     */
    _updateLastEntry: function (patch) {
      if (this.historyItems.length === 0) return
      var last = this.historyItems[this.historyItems.length - 1]
      if (last.status !== 'loading') return
      var keys = Object.keys(patch)
      for (var i = 0; i < keys.length; i++) {
        this.$set(last, keys[i], patch[keys[i]])
      }
    }
  }
}
